import { useState } from 'react';

export interface UseSuccessDialogOptions {
  title?: string;
  description?: string;
  iconColor?: string;
  autoCloseDelay?: number;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

export interface UseSuccessDialogReturn {
  isOpen: boolean;
  showSuccess: (options?: UseSuccessDialogOptions) => void;
  hideSuccess: () => void;
  successOptions: UseSuccessDialogOptions;
}

/**
 * Custom hook for managing SuccessDialog state
 * 
 * @example
 * ```tsx
 * const { isOpen, showSuccess, hideSuccess, successOptions } = useSuccessDialog();
 * 
 * const handleApprove = () => {
 *   // Perform action
 *   showSuccess({
 *     title: "Patient Approved",
 *     description: "The patient has been successfully approved.",
 *     autoCloseDelay: 3000
 *   });
 * };
 * 
 * return (
 *   <>
 *     <button onClick={handleApprove}>Approve Patient</button>
 *     <SuccessDialog
 *       isOpen={isOpen}
 *       onClose={hideSuccess}
 *       {...successOptions}
 *     />
 *   </>
 * );
 * ```
 */
export const useSuccessDialog = (): UseSuccessDialogReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [successOptions, setSuccessOptions] = useState<UseSuccessDialogOptions>({
    title: 'Successfully completed',
    description: 'The operation has been completed successfully.',
    iconColor: '#198754',
    autoCloseDelay: 3000,
    size: 'md',
  });

  const showSuccess = (options?: UseSuccessDialogOptions) => {
    setSuccessOptions(prev => ({
      ...prev,
      ...options,
    }));
    setIsOpen(true);
  };

  const hideSuccess = () => {
    setIsOpen(false);
  };

  return {
    isOpen,
    showSuccess,
    hideSuccess,
    successOptions,
  };
};

export default useSuccessDialog;

